import { TooltipDirective } from '../../../auxiliares/tooltip/tooltip.directive';
import { BOTONES } from '../../../componentes/botones';
import { Component, OnInit, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatTabsModule } from '@angular/material/tabs';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BreadcrumbComponent } from '../../../componentes/breadcrumb/breadcrumb.component';
import { PageTitleComponent } from '../../../componentes/page-title/page-title.component';
import {
  IFilter,
  ILuminaria,
  IModoLuminaria,
  IPopulate,
  IPuesta,
  IQueryParam,
  TipoValoresReporte,
} from 'modelos/src';
import { ListadoReportesGenericosComponent } from '../listado-reportes-genericos/listado-reportes-genericos.component';
import {
  GraficosComponent,
  IPieChartData,
  IBarChartData,
} from '../../../auxiliares/graficos/graficos.component';
import {
  StatCardComponent,
  StatCardConfig,
} from '../../../componentes/stat-card/stat-card.component';
import { LuminariasHttpService } from '../../../auxiliares/servicios/http/luminarias.service';
import { ResumenesDatosService } from '../../../auxiliares/servicios/http/resumenes-datos.service';
import { PuestasService } from '../../../auxiliares/servicios/http/puestas.service';
import { ChartColors } from '../../../auxiliares/servicios/chart-colors.service';
import { LuminariasService } from '../../entidades/luminarias/service';
import {
  CodigoEstadoLuminaria,
  presentacionEstado,
} from '../../entidades/luminarias/estado/estado-luminaria';
import { ILuminariaExtendida } from '../../entidades/luminarias/luminarias.types';
import { GraficoEventosLuminariasComponent } from '../../entidades/luminarias/grafico-eventos-luminarias/grafico-eventos-luminarias.component';
import {
  DatePickerComponent,
  DateRangeValue,
} from '../../../componentes/date-picker/date-picker.component';
import { DashboardFiltrosComponent } from '../../../componentes/dashboard-filtros/dashboard-filtros.component';
import {
  DashboardGrillaComponent,
  DashboardGrillaItem,
} from '../../../componentes/dashboard-grilla/dashboard-grilla.component';
import { DashboardPanelComponent } from '../../../componentes/dashboard-panel/dashboard-panel.component';
import { nivelPerfil } from '../../../auxiliares/iconos/iconos';

export type NivelEntidadDashboard = 'grupoLuminaria' | 'puesta' | 'grupoPuesta';

export interface DashboardEntidadData {
  nivel: NivelEntidadDashboard;
  idEntidad: string;
  nombre?: string;
}

interface GraficoConfig extends DashboardGrillaItem {
  titulo: string;
  /** Ícono del encabezado del panel */
  icono: string;
  /** Mensaje cuando el panel no tiene datos para mostrar */
  sinDatos?: string;
  tipo: 'pie' | 'bar' | 'column' | 'eventos';
  altura?: string;
}

// Cómo se almacena el consumo de cada nivel en los resúmenes (ver gestion-cron).
const CONFIG_CONSUMO: Record<
  NivelEntidadDashboard,
  { agrupacion: 'Grupo' | 'Individual'; tipoEntidad: 'Luminaria' | 'Puesta' }
> = {
  grupoLuminaria: { agrupacion: 'Grupo', tipoEntidad: 'Luminaria' },
  puesta: { agrupacion: 'Individual', tipoEntidad: 'Puesta' },
  grupoPuesta: { agrupacion: 'Grupo', tipoEntidad: 'Puesta' },
};

@Component({
  selector: 'app-dashboard-entidad-luminarias',
  standalone: true,
  imports: [
    TooltipDirective,
    CommonModule,
    BOTONES,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatTabsModule,
    MatTooltipModule,
    FormsModule,
    BreadcrumbComponent,
    PageTitleComponent,
    GraficosComponent,
    StatCardComponent,
    GraficoEventosLuminariasComponent,
    DatePickerComponent,
    ListadoReportesGenericosComponent,
    DashboardFiltrosComponent,
    DashboardGrillaComponent,
    DashboardPanelComponent,
  ],
  templateUrl: './dashboard-entidad-luminarias.component.html',
  styleUrl: './dashboard-entidad-luminarias.component.scss',
})
export class DashboardEntidadLuminariasComponent implements OnInit {
  private luminariasHttp = inject(LuminariasHttpService);
  private luminariasService = inject(LuminariasService);
  private resumenesDatosService = inject(ResumenesDatosService);
  private puestasService = inject(PuestasService);
  private route = inject(ActivatedRoute);
  private router = inject(Router);

  public nivel: NivelEntidadDashboard;
  public idEntidad: string;
  public nombre: string;

  // Reportes de la entidad. idsAsignados del reporte incluye la luminaria, sus
  // grupos, la puesta y los grupos de la puesta, por lo que el mismo id de la
  // entidad (grupo de luminarias / puesta / grupo de puestas) matchea directo.
  // Los tipos se acotan a los que realmente tienen las luminarias del nivel
  // (definirTiposReporte), para que las columnas coincidan con las de luminaria.
  public tiposReporteEstado: TipoValoresReporte[] = [];
  public tiposReporteConsumo: TipoValoresReporte[] = [];

  // Referencia ESTABLE para el input [entidad] de los listados de reportes.
  // Debe ser un objeto fijo: un getter que devuelve {} nuevo en cada ciclo de
  // change detection dispara ngOnChanges en loop y tilda la vista.
  public entidadReporte!: IPuesta;

  public cargando = true;
  public luminarias: ILuminariaExtendida[] = [];
  public cantidadLuminarias = 0;
  // Ids de las luminarias del nivel, para el gráfico de eventos por tipo de alarma.
  public idsLuminariasEventos: string[] = [];

  // Stat cards
  public statCards: StatCardConfig[] = [];

  // Pies
  public datosEstados: IPieChartData[] = [];
  public datosEncendido: IPieChartData[] = [];
  public datosModelos: IPieChartData[] = [];
  public datosDispositivos: IPieChartData[] = [];
  public datosModos: IPieChartData[] = [];

  // Bar / column
  public datosTiempoEncendido: IBarChartData[] = [];
  public categoriasTiempoEncendido: string[] = [];
  public datosConsumo: IBarChartData[] = [];
  public categoriasConsumo: string[] = [];

  // Selector de período del gráfico de consumo (monthrange).
  public hoy = new Date();
  public rangoMesesConsumo: DateRangeValue = (() => {
    const hoy = new Date();
    const inicio = new Date(hoy.getFullYear(), hoy.getMonth() - 5, 1);
    const fin = new Date(hoy.getFullYear(), hoy.getMonth(), 1);
    return { start: inicio.toISOString(), end: fin.toISOString() };
  })();

  // Recarga del consumo al cambiar el período
  public cargandoConsumo = false;

  // Paneles ordenables; el orden lo persiste app-dashboard-grilla
  public graficos: GraficoConfig[] = [];

  constructor() {
    this.nivel = this.route.snapshot.paramMap.get(
      'nivel',
    ) as NivelEntidadDashboard;
    this.idEntidad = this.route.snapshot.paramMap.get('id') ?? '';
    this.entidadReporte = { _id: this.idEntidad } as unknown as IPuesta;
    this.nombre =
      this.route.snapshot.queryParamMap.get('nombre') || this.tituloNivel();
    this.inicializarGraficos();
  }

  // Mismos íconos que el menú de navegación (auxiliares/iconos).
  public iconoNivel(): string {
    return nivelPerfil(this.nivel).icon;
  }

  /** El listado de la entidad, como lo nombra el menú. */
  public labelListado(): string {
    switch (this.nivel) {
      case 'grupoLuminaria':
        return 'Grupos de luminarias';
      case 'puesta':
        return 'Puestas';
      case 'grupoPuesta':
        return 'Grupos de puestas';
    }
  }

  // Seguimiento de los perfiles mandados desde esta entidad (filtro ?fuente=).
  public verSeguimiento(): void {
    this.router.navigate(['main/luminarias/seguimiento/perfiles'], {
      queryParams: { fuente: this.idEntidad },
    });
  }

  /** Adónde vuelven las migas si no se vino de ningún lado. */
  public rutaListado(): string {
    if (this.nivel === 'puesta') return '/main/luminarias/puestas';
    if (this.nivel === 'grupoPuesta') return '/main/luminarias/grupos/Puesta';
    return '/main/luminarias/grupos/Luminaria';
  }

  // ── Paneles del dashboard ───────────────────────────────────────────────────
  private inicializarGraficos(): void {
    this.graficos = [
      {
        id: 'estados',
        sinDatos: 'No hay luminarias para mostrar su estado',
        icono: 'pie_chart',
        titulo: 'Distribución de estados',
        tipo: 'pie',
        altura: '320px',
      },
      {
        id: 'encendido',
        sinDatos: 'No hay información de encendido de las luminarias',
        icono: 'lightbulb',
        titulo: 'Distribución encendidas/apagadas',
        tipo: 'pie',
        altura: '320px',
      },
      {
        id: 'modelos',
        sinDatos: 'No hay luminarias para mostrar sus modelos',
        icono: 'memory',
        titulo: 'Modelos',
        tipo: 'pie',
        altura: '320px',
      },
      {
        id: 'dispositivos',
        sinDatos: 'No hay luminarias para mostrar',
        icono: 'router',
        titulo: 'Luminarias con/sin dispositivo',
        tipo: 'pie',
        altura: '320px',
      },
      {
        id: 'modos',
        sinDatos: 'No hay información del modo de las luminarias',
        icono: 'settings_suggest',
        titulo: 'Distribución por modo',
        tipo: 'pie',
        altura: '320px',
      },
      {
        id: 'tiempoEncendido',
        sinDatos: 'No hay información de horas de encendido',
        icono: 'highlight',
        titulo: 'Luminarias según tiempo de encendido',
        tipo: 'bar',
        altura: '300px',
      },
      {
        id: 'eventosAlarma',
        icono: 'report',
        titulo: 'Eventos por tipo de alarma',
        tipo: 'eventos',
        altura: '320px',
      },
      {
        id: 'consumo',
        sinDatos: 'No hay consumo registrado en el período elegido',
        icono: 'bolt',
        titulo: 'Consumo mensual',
        tipo: 'column',
        altura: '300px',
      },
    ];
  }

  // Todo se carga junto en ngOnInit; sólo el consumo se recarga por separado.
  public graficoListo(id: string): boolean {
    return id === 'consumo' ? !this.cargandoConsumo : true;
  }

  /**
   * Si el panel tiene algo que mostrar. Sin datos se muestra el mensaje del
   * panel en lugar de un gráfico vacío. "Sin dato" no cuenta como dato.
   */
  public tieneDatos(grafico: GraficoConfig): boolean {
    switch (grafico.tipo) {
      case 'pie':
        return (this.obtenerDatosGrafico(grafico.id) as IPieChartData[])?.some(
          (d) => d.y > 0 && d.name !== 'Sin dato',
        );
      case 'bar':
      case 'column':
        return (this.obtenerDatosGrafico(grafico.id) as IBarChartData[])?.some(
          (serie) => serie.data?.some((v) => v > 0),
        );
      default:
        // Los eventos muestran su propio estado vacío
        return true;
    }
  }

  public obtenerDatosGrafico(id: string): any {
    switch (id) {
      case 'estados':
        return this.datosEstados;
      case 'encendido':
        return this.datosEncendido;
      case 'modelos':
        return this.datosModelos;
      case 'dispositivos':
        return this.datosDispositivos;
      case 'modos':
        return this.datosModos;
      case 'tiempoEncendido':
        return this.datosTiempoEncendido;
      case 'consumo':
        return this.datosConsumo;
      default:
        return [];
    }
  }

  public obtenerCategoriasGrafico(id: string): string[] | undefined {
    switch (id) {
      case 'tiempoEncendido':
        return this.categoriasTiempoEncendido;
      case 'consumo':
        return this.categoriasConsumo;
      default:
        return undefined;
    }
  }

  async ngOnInit(): Promise<void> {
    this.cargando = true;
    try {
      await this.cargarLuminarias();
      this.definirTiposReporte();
      this.procesarGraficosLuminarias();
      await this.cargarConsumo();
      this.armarStatCards();
    } catch (error) {
      console.error('Error cargando dashboard de entidad', error);
    } finally {
      this.cargando = false;
    }
  }

  public tituloNivel(): string {
    switch (this.nivel) {
      case 'grupoLuminaria':
        return 'Grupo de luminarias';
      case 'puesta':
        return 'Puesta';
      case 'grupoPuesta':
        return 'Grupo de puestas';
    }
  }

  // ── Carga de luminarias del nivel ──────────────────────────────────────────
  private async cargarLuminarias(): Promise<void> {
    const filter = await this.filtroLuminarias();
    if (!filter) {
      this.luminarias = [];
      this.cantidadLuminarias = 0;
      return;
    }

    const populate: IPopulate[] = [
      { path: 'modeloDispositivo', select: 'marca modelo' },
      {
        path: 'dispositivo',
        select:
          'config deveui fechaUltimaComunicacion tiempoLimiteComunicacion idModeloDispositivo name tipo paquetes.perdidaPaquetes',
      },
    ];
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      populate: JSON.stringify(populate),
      sort: 'identificacion',
      includeChildren: true,
      unlimited: true,
      select: `deveui identificacion idCliente idsGrupos idPuesta tipoEnergizado ubicacion idModeloDispositivo estado ultimoReportePeriodico dispositivo idUltimoReporte ultimoReporteEnergia fechaUltimaComunicacion tiempoEncendida`,
    };

    const data = await this.luminariasHttp.getFiltered(query);
    const luminarias: ILuminariaExtendida[] = data.datos;
    luminarias.forEach((l) => {
      l.estadoCalculado = this.luminariasService.determinarEstadoLuminaria(l);
    });
    this.luminarias = luminarias;
    this.cantidadLuminarias = data.totalCount ?? luminarias.length;
    this.idsLuminariasEventos = luminarias
      .map((l) => l._id)
      .filter((id): id is string => !!id);
  }

  // Acota los tipos de reporte a los tipos de dispositivo presentes en el nivel,
  // así el listado muestra las mismas columnas que el detalle de la luminaria.
  private definirTiposReporte(): void {
    const mapa: Record<
      string,
      { estado: TipoValoresReporte; consumo?: TipoValoresReporte }
    > = {
      'Luminaria GPE': {
        estado: 'Luminaria GPE Periódico',
        consumo: 'Luminaria GPE Energía',
      },
      'Luminaria ACTIS FING': {
        estado: 'Luminaria ACTIS FING Estado',
        consumo: 'Luminaria ACTIS FING Energía',
      },
      'Luminaria CitiLight': {
        estado: 'Luminaria CitiLight Estado',
        consumo: 'Luminaria CitiLight Energía',
      },
      'Luminaria Wellness': { estado: 'Luminaria Wellness' },
    };

    const tiposPresentes = new Set(
      this.luminarias
        .map((l) => l.dispositivo?.tipo)
        .filter((t): t is NonNullable<typeof t> => !!t),
    );

    const estado: TipoValoresReporte[] = [];
    const consumo: TipoValoresReporte[] = [];
    tiposPresentes.forEach((tipo) => {
      const m = mapa[tipo];
      if (!m) return;
      estado.push(m.estado);
      if (m.consumo) consumo.push(m.consumo);
    });

    // Fallback: si no se pudo detectar el tipo, mostrar todos.
    this.tiposReporteEstado = estado.length
      ? estado
      : [
          'Luminaria GPE Periódico',
          'Luminaria ACTIS FING Estado',
          'Luminaria CitiLight Estado',
          'Luminaria Wellness',
        ];
    this.tiposReporteConsumo = consumo.length
      ? consumo
      : estado.length
        ? []
        : [
            'Luminaria GPE Energía',
            'Luminaria ACTIS FING Energía',
            'Luminaria CitiLight Energía',
          ];
  }

  // Filtro de luminarias según el nivel. Grupo de puestas: resuelve primero las
  // puestas del grupo y filtra luminarias por esas puestas.
  private async filtroLuminarias(): Promise<IFilter<ILuminaria> | null> {
    if (this.nivel === 'grupoLuminaria') {
      return { idsGrupos: this.idEntidad } as IFilter<ILuminaria>;
    }
    if (this.nivel === 'puesta') {
      return { idPuesta: this.idEntidad } as IFilter<ILuminaria>;
    }
    // grupoPuesta
    const queryPuestas: IQueryParam = {
      filter: JSON.stringify({ idsGrupos: this.idEntidad }),
      select: '_id',
      includeChildren: true,
      unlimited: true,
    };
    const puestas = (await this.puestasService.getFiltered(queryPuestas)).datos;
    const idsPuestas = puestas.map((p) => p._id).filter((id) => !!id);
    if (!idsPuestas.length) return null;
    return { idPuesta: { $in: idsPuestas } } as unknown as IFilter<ILuminaria>;
  }

  // ── Pies / barras (mismo cálculo que el dashboard de luminarias) ───────────
  private procesarGraficosLuminarias(): void {
    this.procesarEstados();
    this.procesarEncendido();
    this.procesarModelos();
    this.procesarDispositivos();
    this.procesarModos();
    this.procesarTiempoEncendido();
  }

  private procesarEstados(): void {
    const codigos: CodigoEstadoLuminaria[] = [0, 1, 2, 3, 4, 5];
    const conteo: Record<number, number> = {
      0: 0,
      1: 0,
      2: 0,
      3: 0,
      4: 0,
      5: 0,
    };
    this.luminarias.forEach((l) => {
      const codigo = l.estadoCalculado?.codigo;
      if (codigo != null) conteo[codigo]++;
    });
    this.datosEstados = codigos
      .map((c) => ({
        name: presentacionEstado(c).label,
        y: conteo[c],
        color: presentacionEstado(c).color,
      }))
      .filter((d) => d.y > 0);
  }

  private procesarEncendido(): void {
    let encendidas = 0;
    let apagadas = 0;
    let sinDato = 0;
    this.luminarias.forEach((l) => {
      const encendida = this.luminariasService.isEncendida(l);
      if (encendida === true) encendidas++;
      else if (encendida === false) apagadas++;
      else sinDato++;
    });
    this.datosEncendido = [
      { name: 'Encendida', y: encendidas, color: presentacionEstado(4).color },
      { name: 'Apagada', y: apagadas, color: presentacionEstado(5).color },
      { name: 'Sin dato', y: sinDato, color: ChartColors.neutral() },
    ].filter((d) => d.y > 0);
  }

  private procesarModelos(): void {
    const conteo: Record<string, number> = {};
    this.luminarias.forEach((lum) => {
      const marca = lum.modeloDispositivo?.marca ?? 'Sin marca';
      const modelo = lum.modeloDispositivo?.modelo ?? 'Sin modelo';
      const nombre = `${marca} ${modelo}`;
      conteo[nombre] = (conteo[nombre] ?? 0) + 1;
    });
    this.datosModelos = Object.entries(conteo).map(([modelo, cantidad], i) => ({
      name: modelo,
      y: cantidad,
      color: ChartColors.getCategoricalByIndex(i),
    }));
  }

  private procesarDispositivos(): void {
    let con = 0;
    let sin = 0;
    this.luminarias.forEach((lum) => {
      if (lum.deveui && lum.deveui.trim() !== '') con++;
      else sin++;
    });
    this.datosDispositivos = [
      { name: 'Con dispositivo', y: con, color: ChartColors.success() },
      { name: 'Sin dispositivo', y: sin, color: ChartColors.sinDispositivo() },
    ].filter((d) => d.y > 0);
  }

  private procesarModos(): void {
    const SIN_DATO = 'Sin dato';
    const coloresPorModo: Record<string, string> = {
      Astronómico: ChartColors.info(),
      Fotocélula: ChartColors.warning(),
      Manual: ChartColors.color4(),
      'Por Defecto': ChartColors.success(),
      Indeterminado: ChartColors.error(),
      [SIN_DATO]: ChartColors.neutral(),
    };
    const modos: IModoLuminaria[] = [
      'Astronómico',
      'Fotocélula',
      'Manual',
      'Por Defecto',
      'Indeterminado',
    ];
    const conteo: Record<string, number> = {};
    modos.forEach((m) => (conteo[m] = 0));
    conteo[SIN_DATO] = 0;

    this.luminarias.forEach((l) => {
      const modo = l.ultimoReportePeriodico?.valores?.modo;
      if (modo && conteo[modo] !== undefined) conteo[modo]++;
      else conteo[SIN_DATO]++;
    });

    this.datosModos = Object.entries(conteo)
      .map(([modo, cantidad]) => ({
        name: modo,
        y: cantidad,
        color: coloresPorModo[modo] ?? ChartColors.getCategoricalByIndex(0),
      }))
      .filter((d) => d.y > 0);
  }

  private procesarTiempoEncendido(): void {
    const conteo = { menos5000: 0, entre: 0, mas10000: 0 };
    this.luminarias
      .filter((lum) => lum.deveui && lum.deveui.trim() !== '')
      .forEach((lum) => {
        const tiempo = lum.tiempoEncendida;
        if (!tiempo || tiempo < 5000) conteo.menos5000++;
        else if (tiempo <= 10000) conteo.entre++;
        else conteo.mas10000++;
      });
    this.categoriasTiempoEncendido = ['< 5000h', '5000-10000h', '> 10000h'];
    this.datosTiempoEncendido = [
      {
        name: 'Cantidad de luminarias',
        data: [conteo.menos5000, conteo.entre, conteo.mas10000],
        colors: [
          ChartColors.success(),
          ChartColors.warning(),
          ChartColors.error(),
        ],
      },
    ];
  }

  // ── Consumo mensual (resúmenes pre-calculados por el cron) ─────────────────
  private async cargarConsumo(): Promise<void> {
    const cfg = CONFIG_CONSUMO[this.nivel];
    // Filtro plano para no acoplarse a la versión del paquete modelos.
    const filter = {
      tipo: 'Consumo Mensual Luminarias',
      agrupacion: cfg.agrupacion,
      tipoEntidad: cfg.tipoEntidad,
      agrupacionTiempo: 'Mensual',
      idsAsignados: this.idEntidad,
    };
    const query: IQueryParam = {
      filter: JSON.stringify(filter),
      includeChildren: true,
      unlimited: true,
    };

    const resumenes = (
      await this.resumenesDatosService.getFiltered<'Consumo Mensual Luminarias'>(
        query,
      )
    ).datos;

    // Un resumen por mes. Ordenar por periodoInicio e indexar por clave AAAA-MM.
    const ordenados = [...resumenes]
      .filter((r) => !!r.periodoInicio)
      .sort(
        (a, b) =>
          new Date(a.periodoInicio!).getTime() -
          new Date(b.periodoInicio!).getTime(),
      );

    const porMes = new Map<string, (typeof ordenados)[number]>();
    ordenados.forEach((r) => {
      const f = new Date(r.periodoInicio!);
      porMes.set(
        `${f.getFullYear()}-${(f.getMonth() + 1).toString().padStart(2, '0')}`,
        r,
      );
    });

    // Tomar solo los meses dentro del rango elegido, rellenando con 0 los faltantes.
    const mesesRango = this._getMesesEnRango(
      new Date(this.rangoMesesConsumo.start),
      new Date(this.rangoMesesConsumo.end),
    );
    const enRango = mesesRango.map((mes) => {
      const key = `${mes.getFullYear()}-${(mes.getMonth() + 1).toString().padStart(2, '0')}`;
      return porMes.get(key) ?? null;
    });

    this.categoriasConsumo = mesesRango.map(
      (mes) =>
        `${(mes.getMonth() + 1).toString().padStart(2, '0')}/${mes.getFullYear()}`,
    );
    this.datosConsumo = [
      {
        name: 'Consumo',
        data: enRango.map(
          (r) => Math.round((r?.resumen?.consumoTotal ?? 0) * 100) / 100,
        ),
        color: ChartColors.color1(),
      },
    ];

    // El promedio diario usa el último mes con datos reales, sin importar el rango.
    this._consumoUltimoMes = this.calcularConsumoPromedioDiario(ordenados);
  }

  // Lista de meses (día 1) entre start y end, ambos inclusive.
  private _getMesesEnRango(start: Date, end: Date): Date[] {
    const meses: Date[] = [];
    const current = new Date(start.getFullYear(), start.getMonth(), 1);
    const endMonth = new Date(end.getFullYear(), end.getMonth(), 1);
    while (current <= endMonth) {
      meses.push(new Date(current));
      current.setMonth(current.getMonth() + 1);
    }
    return meses;
  }

  public async onRangoMesesChange(rango: DateRangeValue): Promise<void> {
    this.rangoMesesConsumo = rango;
    this.cargandoConsumo = true;
    try {
      await this.cargarConsumo();
    } finally {
      this.cargandoConsumo = false;
    }
  }

  private _consumoUltimoMes?: number;

  // Consumo promedio diario por luminaria del último mes con datos.
  private calcularConsumoPromedioDiario(
    ordenados: {
      periodoInicio?: string;
      resumen?: { consumoTotal?: number; totalDispositivos?: number };
    }[],
  ): number | undefined {
    const ultimo = ordenados[ordenados.length - 1];
    if (!ultimo) return undefined;
    const fecha = new Date(ultimo.periodoInicio!);
    const hoy = new Date();
    const esMesActual =
      fecha.getFullYear() === hoy.getFullYear() &&
      fecha.getMonth() === hoy.getMonth();
    const dias = esMesActual
      ? hoy.getDate()
      : new Date(fecha.getFullYear(), fecha.getMonth() + 1, 0).getDate();
    const totalDisp = ultimo.resumen?.totalDispositivos || 0;
    const consumoTotal = ultimo.resumen?.consumoTotal ?? 0;
    if (!totalDisp || !dias) return undefined;
    return consumoTotal / dias / totalDisp;
  }

  // ── Stat cards ─────────────────────────────────────────────────────────────
  private armarStatCards(): void {
    const cards: StatCardConfig[] = [
      {
        title: 'Cantidad de luminarias',
        value: this.cantidadLuminarias,
        subtitle: 'Total',
        icon: 'lightbulb',
        color: 'neutro',
      },
      {
        title: 'Consumo promedio diario por luminaria',
        // Sin consumo registrado se muestra "Sin datos", no 0
        value: this._consumoUltimoMes || null,
        subtitle: 'kWh',
        icon: 'bolt',
        color: 'neutro',
      },
    ];

    const tasa = this.tasaEntregaPromedio();
    if (tasa !== undefined) {
      cards.push({
        title: 'Tasa de entrega promedio Lorawan',
        value: tasa,
        subtitle: this.estadoTasa(tasa),
        icon: 'cell_tower',
        color: tasa >= 95 ? 'success' : tasa >= 80 ? 'warning' : 'error',
      });
    }

    this.statCards = cards;
  }

  private tasaEntregaPromedio(): number | undefined {
    const tasas = this.luminarias
      .map((l) => l.dispositivo?.paquetes?.perdidaPaquetes)
      .filter((p): p is number => p !== null && p !== undefined)
      .map((p) => 100 - p);
    if (!tasas.length) return undefined;
    return tasas.reduce((acc, t) => acc + t, 0) / tasas.length;
  }

  private estadoTasa(tasa: number): string {
    if (tasa >= 95) return 'Excelente';
    if (tasa >= 80) return 'Aceptable';
    return 'Crítico';
  }
}
